import { useState, type FormEvent } from 'react';
import {
  useGenerateLegalDocument,
  type LegalDocumentKind,
} from '../api/queries/legal-documents';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { DateInput } from './DateInput';
import './LegalDocumentGenerator.css';

const KIND_LABEL: Record<LegalDocumentKind, string> = {
  engagement_letter: 'Engagement letter',
  scope_of_work: 'Statement of work',
  proposal: 'Proposal',
};

const KINDS: LegalDocumentKind[] = ['engagement_letter', 'scope_of_work', 'proposal'];

interface LegalDocumentGeneratorProps {
  projectId: number;
  projectCaseNumber: string | null;
}

function todayLocalEpoch(): number {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime();
}

export function LegalDocumentGenerator({
  projectId,
  projectCaseNumber,
}: LegalDocumentGeneratorProps): JSX.Element | null {
  const generate = useGenerateLegalDocument();

  const [activeKind, setActiveKind] = useState<LegalDocumentKind | null>(null);
  const [effectiveDate, setEffectiveDate] = useState<number | null>(todayLocalEpoch);
  const [scope, setScope] = useState('');
  const [fees, setFees] = useState('');
  const [schedule, setSchedule] = useState('');
  const [terms, setTerms] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!can('document.generate')) return null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (activeKind === null) return;
    setError(null);
    if (scope.trim().length === 0 || fees.trim().length === 0) {
      setError('Scope and fees are required.');
      return;
    }
    if (effectiveDate === null) {
      setError('Pick an effective date.');
      return;
    }

    try {
      const { blob } = await generate.mutateAsync({
        kind: activeKind,
        project_id: projectId,
        effective_date: effectiveDate,
        scope: scope.trim(),
        fees: fees.trim(),
        schedule: schedule.trim() || undefined,
        terms: terms.trim() || undefined,
      });

      // Trigger save dialog using the rendered blob — same UX as the
      // invoice / chain-of-custody download.
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${activeKind}-${projectCaseNumber ?? projectId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } finally {
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }

      // Keep the form open so the user can refine and regenerate; only
      // clear the kind so the buttons reset.
      setActiveKind(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError("You don't have permission to generate documents.");
        return;
      }
      if (err instanceof ApiError && err.status === 400) {
        setError('Validation failed — check scope and fees.');
        return;
      }
      setError('Could not generate document. Try again.');
    }
  };

  return (
    <section className="lgd-block">
      <header className="lgd-header">
        <h3>Legal Documents</h3>
        <span className="lgd-meta">
          Generates a PDF and archives it to the document vault
        </span>
      </header>

      {activeKind === null ? (
        <div className="lgd-buttons">
          {KINDS.map((k) => (
            <button
              key={k}
              type="button"
              className="btn-charcoal-secondary lgd-kind-btn"
              onClick={() => {
                setActiveKind(k);
                setError(null);
              }}
            >
              {KIND_LABEL[k]}
            </button>
          ))}
        </div>
      ) : (
        <form className="lgd-form" onSubmit={onSubmit}>
          <div className="lgd-form-header">
            <span className="lgd-form-title">{KIND_LABEL[activeKind]}</span>
            <button
              type="button"
              className="lgd-link"
              onClick={() => {
                setActiveKind(null);
                setError(null);
              }}
              disabled={generate.isPending}
            >
              ← Pick a different kind
            </button>
          </div>

          <label className="lgd-field">
            <span>Effective date</span>
            <DateInput
              value={effectiveDate}
              onChange={setEffectiveDate}
              disabled={generate.isPending}
            />
          </label>

          <label className="lgd-field">
            <span>{activeKind === 'proposal' ? 'Approach' : activeKind === 'scope_of_work' ? 'Deliverables' : 'Scope of services'} *</span>
            <textarea
              className="form-input lgd-textarea"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              rows={5}
              maxLength={50_000}
              disabled={generate.isPending}
              required
              placeholder="Describe what SFS will perform on this engagement…"
            />
          </label>

          <label className="lgd-field">
            <span>{activeKind === 'proposal' ? 'Fee estimate' : 'Fees'} *</span>
            <textarea
              className="form-input lgd-textarea"
              value={fees}
              onChange={(e) => setFees(e.target.value)}
              rows={4}
              maxLength={50_000}
              disabled={generate.isPending}
              required
              placeholder="Hourly $X, retainer $Y, expenses billed at cost…"
            />
          </label>

          <label className="lgd-field">
            <span>{activeKind === 'scope_of_work' ? 'Milestones' : activeKind === 'proposal' ? 'Timeline' : 'Payment schedule'}</span>
            <textarea
              className="form-input lgd-textarea"
              value={schedule}
              onChange={(e) => setSchedule(e.target.value)}
              rows={3}
              maxLength={50_000}
              disabled={generate.isPending}
            />
          </label>

          <label className="lgd-field">
            <span>{activeKind === 'scope_of_work' ? 'Acceptance criteria' : 'Terms'}</span>
            <textarea
              className="form-input lgd-textarea"
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              rows={3}
              maxLength={50_000}
              disabled={generate.isPending}
            />
          </label>

          {error && <p className="lgd-error" role="alert">{error}</p>}

          <div className="lgd-form-actions">
            <button
              type="submit"
              className="btn-cyan-primary"
              disabled={
                generate.isPending ||
                scope.trim().length === 0 ||
                fees.trim().length === 0
              }
            >
              {generate.isPending ? 'Generating…' : 'Generate & download'}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
